import React, { useState, useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { createPageUrl } from "@/utils/routing";
import { Home, Book, Users, Dices, Scale, LogIn, Map, Sparkles, Settings, BookOpen, FlaskConical, UserPlus, Heart, WandSparkles, User, MessageCircle } from "lucide-react";
import { initMobileFeatures } from "@/utils/mobile";
import { useAuth } from "@/hooks/useAuth";
import { SettingsMenu } from "@/components/ui/SettingsMenu";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarProvider,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import DiceRoller from "@/components/dice/DiceRoller";
import { MobileNavMenu } from "./MobileNavMenu";
import { PWAInstallBanner } from "@/components/shared/PWAInstallBanner";
import { OnlineStatusBanner } from "@/components/shared/OnlineStatusBanner";
import { CommandPalette } from "@/components/ui/CommandPalette";
import { KeyboardShortcutsDialog } from "@/components/ui/KeyboardShortcutsDialog";
import { toast } from "sonner";
import { usePageHeader } from "@/context/PageHeaderContext";
import { getDefaultPageTitle, MYTHKIT_BRAND } from "@/utils/pageTitles";
import { MOBILE_NAV_DICE, MOBILE_NAV_GROUP_OPTIONS_BY_ID, MOBILE_NAV_HOME } from "@/config/mobileNav";
import { readMobileNavPrefs } from "@/utils/mobileNavPrefs";

// Desktop navigation items (unchanged)
const navigationItems = [
  {
    title: "Home",
    url: "/",
    icon: Home,
  },
  {
    title: "Compendium",
    url: createPageUrl("Compendium"),
    icon: Book,
  },
  {
    title: "Rules",
    url: createPageUrl("Rules"),
    icon: Scale,
  },
  {
    title: "My Characters",
    url: createPageUrl("MyCharacters"),
    icon: Users,
  },
  {
    title: "Homebrew",
    url: createPageUrl("Homebrew"),
    icon: FlaskConical,
  },
  {
    title: "Spell Book",
    url: createPageUrl("SpellBook"),
    icon: WandSparkles,
  },
  {
    title: "Campaigns",
    url: createPageUrl("Campaigns"),
    icon: Map,
  },
  {
    title: "Generators",
    url: createPageUrl("Generators"),
    icon: Sparkles,
  },
];

// "More" section: single link to Library & tools page (moved into Settings menu)
const moreNavItems = [
  { title: "Library & tools", url: createPageUrl("More"), icon: BookOpen },
];

const mobileNavActiveTabClass =
  "nav-item nav-item--active font-semibold text-text-primary";

const sidebarNavLinkClass = (isActive, collapsed) =>
  cn(
    "nav-item flex items-center rounded-lg mb-1 touch-manipulation",
    collapsed ? "justify-center p-2 min-h-[2.5rem] w-full" : "gap-3 px-4 py-3",
    isActive ? "nav-item--active font-semibold" : ""
  );

function SidebarAuthFooter({ collapsed, user, hasRealAccount, onOpenKeyboardShortcuts }) {
  const showLogin = !hasRealAccount;

  return (
    <>
      {user && (
        <SidebarMenuItem>
          {collapsed ? (
            <div
              className="flex justify-center py-2 text-text-muted"
              title={user.username || "User"}
            >
              <User className="w-5 h-5 shrink-0" aria-hidden />
            </div>
          ) : (
            <div className="px-4 py-2">
              <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">Account</p>
              <p className="text-sm text-text-muted truncate mt-0.5" title={user.username || "User"}>
                {user.username || "User"}
              </p>
              {user.is_guest && (
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">Guest mode</p>
              )}
            </div>
          )}
        </SidebarMenuItem>
      )}
      {showLogin && (
        <SidebarMenuItem>
          <Link
            to="/login"
            className={sidebarNavLinkClass(false, collapsed)}
            title="Log in"
          >
            <LogIn className="w-5 h-5 shrink-0" />
            {!collapsed && <span>Log in</span>}
          </Link>
        </SidebarMenuItem>
      )}
      <SidebarMenuItem>
        {collapsed ? (
          <div className="flex justify-center py-1">
            <SettingsMenu size="sm" onOpenKeyboardShortcuts={onOpenKeyboardShortcuts} />
          </div>
        ) : (
          <SettingsMenu
            variant="button"
            size="sm"
            onOpenKeyboardShortcuts={onOpenKeyboardShortcuts}
            trigger={
              <button
                type="button"
                className={cn(sidebarNavLinkClass(false, false), "w-full border-0 bg-transparent cursor-pointer")}
                aria-label="Open settings menu"
              >
                <Settings className="w-5 h-5 shrink-0" />
                <span>Settings</span>
              </button>
            }
          />
        )}
      </SidebarMenuItem>
    </>
  );
}

function DesktopSidebar({
  mythkitIconSrc,
  navigationItems,
  moreNavItems,
  location,
  hasRealAccount,
  user,
  onOpenKeyboardShortcuts,
}) {
  const { collapsed } = useSidebar();

  return (
    <Sidebar className="pattern-sidebar no-print hidden md:flex">
      <SidebarHeader className="sidebar-header">
        <div className={cn("flex w-full items-center", collapsed ? "justify-center" : "gap-3")}>
          <Link
            to="/"
            className={cn(
              "flex items-center rounded-lg hover:opacity-90 transition-opacity flex-shrink-0",
              collapsed ? "justify-center" : "gap-3"
            )}
            title="Home"
          >
            <div
              className={cn(
                "rounded-lg flex items-center justify-center shadow-lg overflow-hidden flex-shrink-0",
                collapsed ? "w-9 h-9" : "w-12 h-12"
              )}
            >
              <img src={mythkitIconSrc} alt="Myth-Kit" className="w-full h-full object-contain" />
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <h2 className="font-bold text-2xl heading-primary">Myth-Kit</h2>
                <p className="text-xs text-muted-foreground">D&D 5e Companion</p>
              </div>
            )}
          </Link>
        </div>
      </SidebarHeader>

      <SidebarContent className="sidebar-content">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    className={sidebarNavLinkClass(location.pathname === item.url, collapsed)}
                  >
                    <Link
                      to={item.url}
                      className={sidebarNavLinkClass(location.pathname === item.url, collapsed)}
                      title={item.title}
                    >
                      <item.icon className="w-5 h-5 shrink-0" />
                      {!collapsed && <span>{item.title}</span>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {!collapsed && (
                <SidebarMenuItem>
                  <div className="px-4 py-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                    More
                  </div>
                </SidebarMenuItem>
              )}
              {moreNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    className={sidebarNavLinkClass(location.pathname === item.url, collapsed)}
                  >
                    <Link
                      to={item.url}
                      className={sidebarNavLinkClass(location.pathname === item.url, collapsed)}
                      title={item.title}
                    >
                      <item.icon className="w-5 h-5 shrink-0" />
                      {!collapsed && <span>{item.title}</span>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className={cn("mt-auto border-t border-forest-green/10", collapsed ? "pt-2" : "pt-4")}>
          <SidebarGroupContent>
            <SidebarMenu>
              {import.meta.env.VITE_DISCORD_URL && (
                <SidebarMenuItem>
                  <a
                    href={import.meta.env.VITE_DISCORD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={sidebarNavLinkClass(false, collapsed)}
                    title="Join Discord"
                  >
                    <MessageCircle className="w-5 h-5 shrink-0" />
                    {!collapsed && <span>Join Discord</span>}
                  </a>
                </SidebarMenuItem>
              )}
              {import.meta.env.VITE_PATREON_URL && (
                <SidebarMenuItem>
                  <a
                    href={import.meta.env.VITE_PATREON_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={sidebarNavLinkClass(false, collapsed)}
                    title="Support on Patreon"
                  >
                    <Heart className="w-5 h-5 fill-current shrink-0" />
                    {!collapsed && <span>Support on Patreon</span>}
                  </a>
                </SidebarMenuItem>
              )}
              <SidebarAuthFooter
                collapsed={collapsed}
                user={user}
                hasRealAccount={hasRealAccount}
                onOpenKeyboardShortcuts={onOpenKeyboardShortcuts}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}

function MobileAccountBar({ user, hasRealAccount, onOpenKeyboardShortcuts }) {
  const showLogin = !hasRealAccount;

  return (
    <div className="mobile-account-bar md:hidden no-print border-t border-forest-green/15 bg-primary/95 px-3 sm:px-4 py-2 flex items-center gap-3 min-h-[44px]">
      {user && (
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-text-muted uppercase tracking-wide">Account</p>
          <p className="text-sm text-text-muted truncate">{user.username || "User"}</p>
          {user.is_guest && (
            <p className="text-xs text-amber-700 dark:text-amber-300">Guest mode</p>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 flex-shrink-0">
        {showLogin && (
          <Link
            to="/login"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white bg-forest-green hover:bg-forest-green-light transition-colors touch-manipulation min-h-[40px] whitespace-nowrap"
            style={{ touchAction: "manipulation" }}
          >
            <LogIn className="w-4 h-4" />
            <span>Log in</span>
          </Link>
        )}
        <SettingsMenu size="sm" onOpenKeyboardShortcuts={onOpenKeyboardShortcuts} />
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, refetchUser, hasRealAccount } = useAuth();
  const [diceRollerOpen, setDiceRollerOpen] = useState(false);
  const [mobileNavPrefs, setMobileNavPrefs] = useState(() => readMobileNavPrefs());
  const { headerTitleOverride, setHeaderTitleOverride } = usePageHeader();

  const defaultPageTitle = getDefaultPageTitle(location.pathname);
  const displayedTitle = headerTitleOverride ?? defaultPageTitle;

  useEffect(() => {
    document.title =
      displayedTitle === MYTHKIT_BRAND
        ? MYTHKIT_BRAND
        : `${displayedTitle} · ${MYTHKIT_BRAND}`;
  }, [displayedTitle]);

  useEffect(() => {
    setHeaderTitleOverride(null);
  }, [location.pathname, setHeaderTitleOverride]);

  // Cache-bust the Mythkit header icon per build. WebView/PWA can otherwise keep serving
  // the old image from HTTP or service-worker caches.
  const mythkitIconVersion =
    (typeof document !== "undefined" &&
      document.querySelector('meta[name="build-time"]')?.getAttribute("content")) ||
    (typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "1.0.0");
  const mythkitIconSrc = `/Mythkit-transperant.png?v=${mythkitIconVersion}`;

  // Initialize mobile features
  useEffect(() => {
    initMobileFeatures()
      .catch(() => {
        // Mobile features initialization failed (non-critical)
      });
  }, []);

  // After Stripe checkout success, Stripe redirects to /?upgraded=1. Refetch user (server updated by webhook), toast, then clear param so user is back in the app.
  const upgradedHandled = React.useRef(false);
  useEffect(() => {
    if (searchParams.get('upgraded') !== '1' || upgradedHandled.current) return;
    upgradedHandled.current = true;
    (async () => {
      try {
        await refetchUser();
        toast.success("You're now a Pro member!", { description: "Enjoy unlimited characters, campaigns, and homebrew." });
      } catch {
        toast.success("Thank you for your purchase!", { description: "Your account will update shortly." });
      }
      const next = new URLSearchParams(searchParams);
      next.delete('upgraded');
      setSearchParams(next, { replace: true });
    })();
  }, [searchParams, refetchUser, setSearchParams]);

  // Check if we're on pages where dice roller should be hidden
  const isOnCombatTracker = location.pathname === createPageUrl("CombatTracker");
  const isOnCharacterCreator =
    location.pathname === createPageUrl("CharacterCreator") ||
    location.pathname === createPageUrl("CharacterCreatorRandomize");
  const shouldShowDiceRoller = !isOnCombatTracker && !isOnCharacterCreator;

  // Check if we're on CharacterSheet page - hide mobile nav there (it has its own navigation)
  const isOnCharacterSheet = location.pathname === createPageUrl("CharacterSheet") ||
                              location.pathname.includes('/character-sheet');
  const isOnCampaignDetail =
    location.pathname === createPageUrl("CampaignDetail") ||
    location.pathname.includes("/campaign-detail");
  const isOnMythkitBoard =
    isOnCharacterSheet || (isOnCampaignDetail && searchParams.get("board") === "1");

  // Check if we're on CampaignDetail - hide app mobile nav there (campaign has its own bottom nav)

  const showDiceRoller = shouldShowDiceRoller && !isOnMythkitBoard;
  const showMobileDice = showDiceRoller;

  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "?" || !e.shiftKey) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) {
        return;
      }
      e.preventDefault();
      setShortcutsOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const syncPrefs = () => setMobileNavPrefs(readMobileNavPrefs());
    window.addEventListener("storage", syncPrefs);
    window.addEventListener("mythkit:mobile-nav-updated", syncPrefs);
    return () => {
      window.removeEventListener("storage", syncPrefs);
      window.removeEventListener("mythkit:mobile-nav-updated", syncPrefs);
    };
  }, []);

  const mobileGroupNavItems = mobileNavPrefs.groupOrder
    .map((groupId) => MOBILE_NAV_GROUP_OPTIONS_BY_ID[groupId])
    .filter(Boolean);

  const isAuthRoute =
    location.pathname === "/login" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/reset-password" ||
    /^\/campaigns\/[^/]+\/join$/.test(location.pathname);

  if (isAuthRoute) {
    return (
      <div className="min-h-screen bg-bg-page text-text-primary">
        <CommandPalette />
        <KeyboardShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
        {children}
      </div>
    );
  }

  return (
    <SidebarProvider>
      <CommandPalette />
      <KeyboardShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <div className="layout-container">
        <a href="#main-content" className="skip-link-main">
          Skip to main content
        </a>
        <div className="layout-shell">
          <OnlineStatusBanner />
          {!hasRealAccount && (
            <div className="layout-banner w-full shrink-0 border-y border-amber-700/30 bg-amber-100/90 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100 px-3 sm:px-4 py-2 text-xs sm:text-sm no-print">
              <div className="max-w-screen-2xl mx-auto flex items-center justify-between gap-3">
                <span>
                  Playing locally. Create an account to sync and join campaigns.
                </span>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 font-semibold underline underline-offset-2 hover:text-amber-700 dark:hover:text-amber-200 whitespace-nowrap"
                >
                  <UserPlus className="w-4 h-4" />
                  Create Account
                </Link>
              </div>
            </div>
          )}
          <div className="layout-body">
            <DesktopSidebar
              mythkitIconSrc={mythkitIconSrc}
              navigationItems={navigationItems}
              moreNavItems={moreNavItems}
              location={location}
              hasRealAccount={hasRealAccount}
              user={user}
              onOpenKeyboardShortcuts={() => setShortcutsOpen(true)}
            />

            <main
              className={`main-content${isOnMythkitBoard ? ' main-content--board' : ''}`}
              data-page={location.pathname === "/" ? "home" : undefined}
              data-board-workspace={isOnMythkitBoard ? 'true' : undefined}
            >
          {!isOnMythkitBoard && (
            <header className="mobile-header md:hidden no-print">
              <Link to="/" className="flex items-center gap-2 sm:gap-3 w-full min-w-0 rounded-lg hover:opacity-90 transition-opacity">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                  <img src={mythkitIconSrc} alt="Myth-Kit" className="w-full h-full object-contain" />
                </div>
                <h1 className="text-lg sm:text-xl font-bold heading-primary truncate min-w-0">
                  {displayedTitle}
                </h1>
              </Link>
            </header>
          )}

          <div
            id="main-content"
            className={`app-main-scroll flex-1 outline-none min-h-0 min-w-0 w-full${isOnMythkitBoard ? ' overflow-hidden' : ''}`}
            style={{ WebkitOverflowScrolling: 'touch' }}
            tabIndex={-1}
          >
            {children}
          </div>

          {!isOnCharacterSheet && !isOnCampaignDetail && (
            <MobileAccountBar
              user={user}
              hasRealAccount={hasRealAccount}
              onOpenKeyboardShortcuts={() => setShortcutsOpen(true)}
            />
          )}

          {/* Mobile Navigation - Hidden on CharacterSheet and CampaignDetail (they have their own nav) */}
          {!isOnCharacterSheet && !isOnCampaignDetail && (
            <nav className="mobile-nav md:hidden no-print">
              <div className="grid gap-1.5 w-full px-2 sm:px-4 py-2 min-h-[44px]">
                <Link
                  to={MOBILE_NAV_HOME.url}
                  className={`flex flex-col items-center justify-center gap-1 py-2 rounded-lg transition-all min-h-[44px] touch-manipulation min-w-0 ${
                    location.pathname === MOBILE_NAV_HOME.url
                      ? `${mobileNavActiveTabClass} font-semibold`
                      : 'nav-item text-text-muted'
                  }`}
                  style={{ touchAction: 'manipulation' }}
                >
                  <span className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                    <MOBILE_NAV_HOME.icon className="w-5 h-5" aria-hidden />
                  </span>
                  <span className="text-[9px] sm:text-[10px] font-medium truncate w-full text-center whitespace-nowrap">
                    {MOBILE_NAV_HOME.title}
                  </span>
                </Link>
                {mobileGroupNavItems.map((item) => (
                  <MobileNavMenu
                    key={item.id}
                    title={item.title}
                    icon={item.icon}
                    items={item.items}
                  />
                ))}
                {showMobileDice && (
                  <button
                    onClick={() => setDiceRollerOpen(true)}
                    className={`flex flex-col items-center justify-center gap-1 py-2 rounded-lg transition-all min-h-[44px] touch-manipulation min-w-0 ${
                      diceRollerOpen
                        ? `${mobileNavActiveTabClass} font-semibold`
                        : 'nav-item text-text-muted'
                    }`}
                    aria-label="Open Dice Roller"
                    style={{ touchAction: 'manipulation' }}
                  >
                    <span className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                      <MOBILE_NAV_DICE.icon className="w-5 h-5" aria-hidden />
                    </span>
                    <span className="text-[9px] sm:text-[10px] font-medium leading-tight">{MOBILE_NAV_DICE.title}</span>
                  </button>
                )}
              </div>
            </nav>
          )}
            </main>
          </div>
        </div>

        {showDiceRoller && (
          <button
            onClick={() => setDiceRollerOpen(true)}
            className="dice-roller dice-roller-desktop no-print hidden md:block"
            aria-label="Open Dice Roller"
          >
            <Dices className="w-8 h-8" />
          </button>
        )}

        {/* Dice Roller Modal */}
        <DiceRoller isOpen={diceRollerOpen} onClose={() => setDiceRollerOpen(false)} />

        <PWAInstallBanner />
      </div>
    </SidebarProvider>
  );
}

